import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Modal,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const upper = (v) => String(v || "").trim().toUpperCase();

const PaymentAccountPicker = ({
  forma,
  value,
  onSelect,
  label,
  sx,
  inputSx,
  disabled = false,
  origen,
  tipo,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [modo, setModo] = useState("");

  const [resultModal, setResultModal] = useState({
    open: false,
    ok: false,
    title: "",
    message: "",
  });

  const closeResultModal = () => setResultModal({ open: false, ok: false, title: "", message: "" });

  const formaU = upper(forma);
  const isTransfer = formaU === "TRANSFERENCIA";

  const title = useMemo(() => {
    if (isTransfer) return "Seleccionar banco";
    return "Seleccionar cuenta";
  }, [isTransfer]);

  useEffect(() => {
    setOpen(false);
    setLoading(false);
    setRows([]);
    setModo("");
  }, [forma]);

  const openPicker = async () => {
    if (!forma || disabled) return;

    setOpen(true);
    setLoading(true);
    setRows([]);
    setModo("");

    try {
      const qs = new URLSearchParams();
      if (origen) qs.set("origen", upper(origen));
      if (tipo) qs.set("tipo", upper(tipo));

      const url = `/cuentas/pagos/cuentas/${encodeURIComponent(formaU)}/${qs.toString() ? `?${qs.toString()}` : ""}`;
      const r = await fetch(url, { credentials: "include" });
      const ct = String(r.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) throw new Error("RESP_NOT_JSON");
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || "NO_SE_PUDO_CARGAR");

      setModo(upper(j?.modo || ""));
      setRows(Array.isArray(j?.results) ? j.results : []);
    } catch (e) {
      setRows([]);
      setResultModal({
        open: true,
        ok: false,
        title: "No se pudieron cargar cuentas",
        message: e?.message || "No se pudieron cargar las cuentas.",
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const selectRow = (row) => {
    const m = upper(modo);

    if (isTransfer || m === "BANCO") {
      const bancoId = String(row?.id || "");
      const cuentaContableId = String(row?.cuenta_contable_id || "");
      const bancoNombre = String(row?.banco || row?.nombre || "");
      const numero = String(row?.numero_cuenta || "");
      const tipoCuenta = String(row?.tipo_cuenta || "");
      const ciudad = String(row?.ciudad || "");
      const cuentaContableNombre = String(row?.cuenta_contable_nombre || "");

      const display = [
        bancoNombre,
        numero ? `- ${numero}` : "",
        tipoCuenta ? `(${tipoCuenta})` : "",
        ciudad ? `• ${ciudad}` : "",
      ]
        .filter(Boolean)
        .join(" ")
        .trim();

      onSelect?.({
        cuentaId: cuentaContableId,
        cuenta: display,
        bancoId,
        banco: {
          id: bancoId,
          banco: bancoNombre,
          numero_cuenta: numero,
          tipo_cuenta: tipoCuenta,
          ciudad,
          cuenta_contable_id: cuentaContableId,
          cuenta_contable_nombre: cuentaContableNombre,
          nombre: String(row?.nombre || bancoNombre),
        },
      });
    } else {
      onSelect?.({
        cuentaId: String(row?.id || ""),
        cuenta: String(row?.nombre || ""),
        grupo: String(row?.grupo || ""),
        codigo: String(row?.codigo || ""),
      });
    }

    setOpen(false);
  };

  return (
    <>
      <TextField
        label={label}
        value={value || ""}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={openPicker} disabled={!forma || disabled}>
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={sx}
        inputProps={inputSx}
      />

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 860,
            maxWidth: "94vw",
            bgcolor: "#fff",
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography sx={{ fontWeight: 800, color: "#1565C0", mb: 2 }}>{title}</Typography>

          <TableContainer sx={{ border: "1px solid #e6e6e6", borderRadius: 1, maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                {isTransfer ? (
                  <TableRow>
                    <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800, width: 180 }}>
                      Número
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800, width: 220 }}>
                      Banco
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800 }}>
                      Detalle
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800, width: 180 }}>
                      Código
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800 }}>Cuenta</TableCell>
                    <TableCell sx={{ backgroundColor: "#f2f2f2", fontWeight: 800, width: 240 }}>
                      Grupo
                    </TableCell>
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ py: 6 }}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography sx={{ fontWeight: 800, color: "#222" }}>No hay resultados para mostrar</Typography>
                        <Typography sx={{ color: "#666", mt: 0.5 }}>
                          Configura cuentas para esta forma de pago.
                        </Typography>
                        <Button
                          variant="outlined"
                          sx={{
                            mt: 2,
                            textTransform: "none",
                            borderColor: "#1565C0",
                            color: "#1565C0",
                            "&:hover": { borderColor: "#0D47A1", color: "#0D47A1" },
                          }}
                          onClick={openPicker}
                          disabled={loading}
                        >
                          {loading ? "Cargando..." : "Cargar"}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={String(row?.id || Math.random())}
                      hover
                      onClick={() => selectRow(row)}
                      sx={{ cursor: "pointer" }}
                    >
                      {isTransfer ? (
                        <>
                          <TableCell>{String(row?.numero_cuenta || "")}</TableCell>
                          <TableCell>{String(row?.banco || row?.nombre || "")}</TableCell>
                          <TableCell>
                            {[
                              row?.tipo_cuenta ? `Tipo: ${String(row.tipo_cuenta)}` : "",
                              row?.ciudad ? `Ciudad: ${String(row.ciudad)}` : "",
                              row?.cuenta_contable_nombre ? `Contable: ${String(row.cuenta_contable_nombre)}` : "",
                            ]
                              .filter(Boolean)
                              .join(" • ")}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{String(row?.codigo || "")}</TableCell>
                          <TableCell>{String(row?.nombre || "")}</TableCell>
                          <TableCell>{String(row?.grupo || "")}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="outlined" onClick={() => setOpen(false)} sx={{ textTransform: "none" }}>
              Cerrar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Dialog open={resultModal.open} onClose={closeResultModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: "#d32f2f" }}>{resultModal.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, color: "#444" }}>{resultModal.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeResultModal}
            variant="contained"
            sx={{
              bgcolor: "#d32f2f",
              "&:hover": { bgcolor: "#b71c1c" },
              textTransform: "none",
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentAccountPicker;
